// Performance disclosure wording shared by the Portfolio Viewer and Portfolio Dashboard
// (on screen and in the Viewer's Excel export), so the two can't drift apart.

// The general performance note (formerly PERF_GENERAL_NOTE) is managed in
// Admin > Disclaimers (perf_general_note); use useDisclaimer('perf_general_note').

// Prefixed to the older registry source fragments that begin "They are ...".
export const PERF_FEE_SENTENCE =
  'The model portfolio returns shown are net of underlying fund manager fees but gross of model management fees.';

export const RISK_METHOD_NOTE =
  'Risk metrics are calculated from monthly returns since inception. Volatility and tracking error are ' +
  'annualised; Return / Risk is the annualised since-inception return divided by volatility (cumulative ' +
  'return where the history is under 12 months).';

// Registry `performance_source` (Investment Config) holds two formats: full statements
// that carry their own fee basis (often self-labelled, e.g. "Compass ETF (50/70):
// Performance is before ...") and older fragments ("They are calculated from the model
// portfolio within ...") that continue the standard fee sentence.
const sourceSentence = (text) => (/^They\b/.test(text) ? `${PERF_FEE_SENTENCE} ${text}` : text);
const selfLabelled = (text) => /^[^.:]{1,80}:\s/.test(text);

/**
 * One line per distinct registry source text among `codes`.
 * codeToSource: {portfolio_code: performance_source}; nameOf: code -> display name.
 * Portfolio names are prefixed only when the group mixes sources and the text
 * doesn't already name its portfolios.
 */
export function buildSourceLines(codes, codeToSource, nameOf) {
  const bySource = new Map();
  codes.forEach((code) => {
    const src = (codeToSource[code] || '').trim();
    bySource.set(src, [...(bySource.get(src) || []), code]);
  });
  const several = bySource.size > 1;
  return [...bySource.entries()].map(([src, srcCodes]) => {
    const names = srcCodes.map(nameOf).join(' & ');
    if (!src) return `${names}: performance source not recorded.`;
    const sentence = sourceSentence(src);
    return several && !selfLabelled(src) ? `${names}: ${sentence}` : sentence;
  });
}

/** {portfolio_code: performance_source} from a registry payload. */
export function registrySourceMap(registryData) {
  const m = {};
  (registryData?.suites || []).forEach((s) => s.portfolios.forEach((p) => {
    if (p.performance_source) m[p.portfolio_code] = p.performance_source;
  }));
  return m;
}

/** 'YYYY-MM' -> 'Nov 2022' */
export function fmtGapMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

/** Footnotes for financial years left blank because a month has no reported return. */
export function fyGapNotes(codes, fyReturnsByCode, nameOf) {
  return codes.flatMap((code) =>
    (fyReturnsByCode[code]?.columns || []).filter((c) => c.gap_months?.length)
      .map((c) => `${nameOf(code)}: ${c.label} not shown (no return reported for ${c.gap_months.map(fmtGapMonth).join(', ')}).`));
}

/** Footnotes for risk metrics computed around missing months. */
export function riskGapNotes(codes, riskByCode, nameOf) {
  return codes.filter((c) => riskByCode[c]?.gap_months?.length).map((c) =>
    `${nameOf(c)}: no return reported for ${riskByCode[c].gap_months.map(fmtGapMonth).join(', ')}. ` +
    'That month is excluded from volatility and tracking error, and Return / Risk is not shown.');
}

/**
 * FY table footer text with {partial_year} filled. The text comes from the API
 * (the portfolio's own footer, else Admin > Disclaimers); a portfolio's own
 * footer has no token and prints as typed. Missing text shows a visible marker.
 */
export function fyFooterWithPartial(text, hasPartial, note = '*Partial year.') {
  if (!text || !text.trim()) return '[FY table footer not resolved]';
  return text.replace('{partial_year}', hasPartial ? note : '').replace(/[ \t]{2,}/g, ' ').trim();
}
